import { format, parseISO } from 'date-fns'
import { isEmpty, isNil } from 'lodash'
import queryString from 'query-string'

import { REQUEST_STATUS } from '@/reducers/voiceAgents/voiceAgents.constants'

// =============================================================================
// Pure helpers for prompt change requests ("comments"). The BE statuses are
// finer-grained than the UI: open/in_review both read as "Open", and
// resolved/rejected both read as "Resolved".
// =============================================================================

export const UI_STATUS = {
  OPEN: 'open',
  RESOLVED: 'resolved',
}

export const getUiStatus = (request) => {
  const terminal =
    request?.status === REQUEST_STATUS.RESOLVED || request?.status === REQUEST_STATUS.REJECTED
  return terminal ? UI_STATUS.RESOLVED : UI_STATUS.OPEN
}

export const isOpenRequest = (request) => getUiStatus(request) === UI_STATUS.OPEN

// Open-comment count for a tab badge. Open-only (matches the default Open
// filter): a number always means "needs attention" and the badge hides at 0.
export const countOpenRequests = (items = []) => items.filter(isOpenRequest).length

// Human label per BE request_type, for the card's comment-type badge:
//   general   → whole-component / agent-level note
//   inline    → highlighted span of prompt text
//   transcript→ highlighted assistant turn in a call transcript
export const COMMENT_TYPE_LABEL = {
  general: 'Basic',
  inline: 'Prompt',
  transcript: 'Transcript',
}

export const getCommentTypeLabel = (request) => COMMENT_TYPE_LABEL[request?.type] || 'Basic'

// A transcript comment whose spoken line the server could not trace back to a
// node (AVR-878). It still belongs to the agent and still shows on the call —
// it just has no prompt to sit next to until someone links it.
export const isUnassignedTranscript = (request) =>
  request?.type === 'transcript' && isNil(request?.target?.id)

// "0:09" — where in the call the commented turn starts. Transcript comments
// carry turn.startMs; the call page prints the same clock next to the turn.
export const formatTurnTimestamp = (startMs) => {
  if (!Number.isFinite(startMs)) {
    return null
  }
  const totalSeconds = Math.floor(startMs / 1000)
  const seconds = String(totalSeconds % 60).padStart(2, '0')
  return `${Math.floor(totalSeconds / 60)}:${seconds}`
}

// Drawer tab key ↔ BE target_type. Comments attach to a node's prompt only
// (AVR-421 scope): target_id is the node id, target_type is 'prompt'. The BE
// also accepts tool/variable targets — add them here when a ticket calls for
// per-tab comments beyond Prompt.
export const TARGET_TYPE_BY_TAB = {
  prompt: 'prompt',
}

export const DRAWER_TAB_BY_TARGET_TYPE = {
  prompt: 'prompt',
}

export const filterRequestsByTarget = (items, targetType, targetId) => {
  if (isEmpty(items)) {
    return []
  }
  return items.filter(
    (request) => request?.target?.type === targetType && request?.target?.id === targetId
  )
}

// Open-comment count for one node + target (the per-node drawer tab badge).
export const countOpenRequestsForTarget = (items, targetType, targetId) =>
  filterRequestsByTarget(items, targetType, targetId).filter(isOpenRequest).length

// "April 8th, 2025 | 12:09pm" — the comment-card timestamp from the design.
export const formatRequestDate = (iso) => {
  if (isNil(iso)) {
    return '—'
  }
  try {
    return format(parseISO(iso), "MMMM do, yyyy '|' h:mmaaa")
  } catch (parseError) {
    return '—'
  }
}

// Deep-link search string for a request: lands on the call-flow tab with the
// target node selected, the matching drawer tab open, and the request focused
// in the Comments view. Consumed by parseChangeRequestFocus on page mount.
export const buildChangeRequestSearch = (request) =>
  queryString.stringify({
    tab: 'call-flow',
    node: request?.target?.id,
    drawerTab: DRAWER_TAB_BY_TARGET_TYPE[request?.target?.type] || 'prompt',
    view: 'comments',
    request: request?.id,
  })

// Deep-link from a transcript comment to the call it anchors to: lands on the
// Call Explorer's Transcript Comments tab with the comment focused (the page
// reads `comment` after the comment list loads). `agent_comments` mirrors
// CALL_EXPLORER_TAB_ENUMS.AGENT_COMMENTS (kept literal to avoid a cross-view import).
export const buildTranscriptCommentCallLink = (request) =>
  `/calls/${request?.call?.id}?${queryString.stringify({
    tab: 'agent_comments',
    comment: request?.id,
  })}`

// queryString.parse returns arrays for repeated params — collapse to the
// first value so a crafted/malformed URL can't push arrays into focus state.
const firstParamValue = (value) => (Array.isArray(value) ? value[0] : value)

export const parseChangeRequestFocus = (search) => {
  const parsed = queryString.parse(search)
  const node = firstParamValue(parsed.node)
  const drawerTab = firstParamValue(parsed.drawerTab)
  const view = firstParamValue(parsed.view)
  const request = firstParamValue(parsed.request)
  if (!node || view !== 'comments') {
    return null
  }
  // AVR-421 scope: 'prompt' is the only commentable target, so an unrecognized
  // drawerTab falls back to it. Future tabs (tool/variable) → add to TARGET_TYPE_BY_TAB.
  const tabKey = TARGET_TYPE_BY_TAB[drawerTab] ? drawerTab : 'prompt'
  const requestId = request ? Number(request) : NaN
  return {
    nodeId: node,
    tabKey,
    requestId: Number.isFinite(requestId) ? requestId : null,
  }
}
